"use client";

import { Box, Link as MuiLink, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { DynamicBlock } from "./DynamicBlock";
import type { PublisherProduct } from "../types/Publisher";

interface PublisherProductsProps {
  products: PublisherProduct[];
  productsTagline: string | null;
}

export function PublisherProducts({ products, productsTagline }: PublisherProductsProps) {
  const posthog = usePostHog();

  const handleProductLinkClick = (product: PublisherProduct) => {
    posthog?.capture("publisher_product_link_clicked", {
      product_title: product.title,
      product_price: product.price,
      product_url: product.link,
    });
  };

  if (!products || products.length === 0) return null;

  return (
    <DynamicBlock title={productsTagline || "Lançamentos"}>
      <Box display="flex" justifyContent="center" gap={2} flexDirection="column">
        {products.map((product) => {
          const productUrl = new URL(product.link);
          productUrl.searchParams.append("utm_source", "leiturascristas");

          return (
            <Box key={product.link}>
              <MuiLink
                href={productUrl.toString()}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                color="textPrimary"
                onClick={() => handleProductLinkClick(product)}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <img
                  src={product.imageUrl}
                  alt={product.title}
                  style={{ width: "100px", height: "100px", objectFit: "contain", flexShrink: 0 }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {product.title}
                  </Typography>
                  {product.price ? (
                    <Typography variant="body2" sx={{ fontWeight: "bold", mt: 0.5, color: "primary.main" }}>
                      {(() => {
                        const numericPrice = parseFloat(product.price.replace(/[^\d.,]/g, "").replace(",", "."));
                        return isNaN(numericPrice)
                          ? product.price
                          : new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(numericPrice);
                      })()}
                    </Typography>
                  ) : null}
                </Box>
              </MuiLink>
            </Box>
          );
        })}
      </Box>
    </DynamicBlock>
  );
}
